import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as MuiButton } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import moment from 'moment-timezone';

import {
  roleToNameInCentral,
  ssuRoles
} from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddRole/roleUtils';
import { financeAccessFieldToName } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/constants';
import { preparePersonnelInfoState } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/utils';

import { UserApiApi } from '../../../../../../../api';
import NotificationManager from '../../../../../../../common/notifications/NotificationManager';
import { dateTime12 } from '../../../../../../../constants/dateFormat';
import { DATA_SAVED, SOMETHING_WENT_WRONG } from '../../../../../../../constants/notificationMessages';
import {
  ROLE_OPERATION_MANAGER,
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_SITE_FINANCIAL_COORDINATOR,
  ROLE_STUDY_MANAGER
} from '../../../../../../../constants/userRoles';
import { NS_COMMON, NSPS_SITE } from '../../../../../../../services/i18n/namespaces';
import { getTittleByRole } from '../../../../../../../services/personnelService';
import { isFinanceAccessRole } from '../../../../../../../services/userRoleService';

import './CurrentRoles.scss';

export default function CurrentRoles({ personnelId, roles, setPersonnelInfo, setRoles }) {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        field: 'studyName',
        headerName: t('study', { ns: NS_COMMON }),
        flex: 1,
        valueGetter(value) {
          return value || '-';
        }
      },
      {
        field: 'siteName',
        headerName: t('site', { ns: NS_COMMON }),
        flex: 1,
        valueGetter(value) {
          return value || '-';
        }
      },
      {
        field: 'role',
        headerName: t('addOrEditPersonnel.role', { ns: NSPS_SITE }),
        flex: 1,
        valueGetter(value) {
          return getTittleByRole(value) || '-';
        }
      },
      {
        field: 'startDate',
        headerName: t('startDate', { ns: NS_COMMON }),
        flex: 1,
        valueGetter: getFormattedDate
      },
      {
        field: 'endDate',
        headerName: t('addOrEditPersonnel.expireDate', { ns: NSPS_SITE }),
        flex: 1,
        valueGetter: getFormattedDate
      },
      {
        field: 'financeAccess',
        headerName: t('addOrEditPersonnel.financeAccess', { ns: NSPS_SITE }),
        flex: 1,
        valueGetter: getFinanceAccessDisplayValues
      },
      {
        field: 'groupAssign',
        headerName: t('addOrEditPersonnel.group', { ns: NSPS_SITE }),
        flex: 1,
        valueGetter(value) {
          return value ? `${value.name} (${value.type})` : '-';
        }
      },
      {
        field: 'actions',
        headerName: t('addOrEditPersonnel.currentRoles.actions', { ns: NSPS_SITE }),
        flex: 1,
        renderCell(row) {
          return (
            <MuiButton
              variant="text"
              sx={{ textTransform: 'none' }}
              disabled={!ssuRoles.includes(row.row.role)}
              onClick={() => {
                UserApiApi.expireSsuRole(row.row.ssuId, personnelId, { role: roleToNameInCentral[row.row.role] })
                  .then(({ data }) => {
                    NotificationManager.success(DATA_SAVED);
                    setPersonnelInfo(preparePersonnelInfoState(data.personnelInfo));
                    setRoles(data?.roles);
                  })
                  .catch(err => {
                    if (err.response.status === 400) {
                      NotificationManager.error('Errors: ' + err.response.data.errors.join('; '));
                    } else if (err.response.status === 404) {
                      NotificationManager.error(err.response.data.message);
                    } else {
                      NotificationManager.error(SOMETHING_WENT_WRONG);
                    }
                  });
              }}
            >
              {t('addOrEditPersonnel.currentRoles.expire', { ns: NSPS_SITE })}
            </MuiButton>
          );
        }
      }
    ],
    [t, personnelId, setPersonnelInfo, setRoles]
  );

  return (
    <>
      <div className="add-or-edit-personnel-modal-section-header">
        <h5 className="add-or-edit-personnel-modal-section-title">
          {t('addOrEditPersonnel.currentRoles.title', { ns: NSPS_SITE })}
        </h5>
      </div>
      <div className="add-or-edit-personnel-modal-section-body add-or-edit-personnel-modal-section-body--no-padding">
        <DataGridPremium
          rows={roles || []}
          columns={columns}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
          getRowId={row => row.studySitePersonnelId}
          pagination
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'studyName', sort: 'asc' }]
            },
            density: 'compact'
          }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              excelOptions: { disableToolbarButton: true }
            }
          }}
        />
      </div>
    </>
  );
}

const baseList = ['financeDetailsAccess', 'remittanceNotification'];

const financeAccessOptions = {
  [ROLE_STUDY_MANAGER]: ['patientPaymentAccess', 'expensesAccess', ...baseList],
  [ROLE_PRINCIPAL_INVESTIGATOR]: baseList,
  [ROLE_OPERATION_MANAGER]: ['expensesAccess', ...baseList],
  [ROLE_SITE_FINANCIAL_COORDINATOR]: baseList
};

function getFinanceAccessDisplayValues(financeAccessDto, row) {
  if (!isFinanceAccessRole(row.role)) {
    return '-';
  }
  const options = financeAccessOptions[row.role];
  return (
    Object.keys(financeAccessDto)
      .filter(key => options.includes(key) && financeAccessDto[key])
      .map(key => financeAccessFieldToName[key])
      .join(', ') || '-'
  );
}

function getFormattedDate(date) {
  return date ? moment(date).format(dateTime12) : '-';
}
